import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@osmix/ui/dialog";
import { Download16Icon, Star32Icon, XIcon, XSmall16Icon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Chip } from "@/components/ui/chip";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

// Предмет отзыва — товар или магазин. Поля опциональны: для магазина нет
// варианта/размера, для товара они приходят из выбранной позиции.
export type ReviewSubject = {
  photo?: string;
  title: string;
  subtitle?: string;
  variant?: string;
  size?: string;
};
const MAX_MEDIA = 10;

// Загруженное фото/видео отзыва (url из хранилища + тип для превью).
export type ReviewMediaItem = {
  url: string;
  kind: "photo" | "video";
};

// Модалка «Оставить отзыв» (Figma leave-review). Карточка предмета + выбор
// звёзд, текст, теги-пилюли, загрузка фото/видео и анонимность. Отправка:
// стандалон-ручки создания отзыва на бэкенде нет (отзыв привязан к заказу —
// api.buyer.reviewOrder), поэтому onSubmit опционален (backend gap).
export function LeaveReviewModal({
  open,
  onOpenChange,
  subject,
  onSubmit,
  initialRating,
  onUploadMedia
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  subject: ReviewSubject;
  onSubmit?: (input: {
    rating: number;
    text: string;
    anonymous: boolean;
    media: ReviewMediaItem[];
  }) => Promise<void> | void;
  // Оценка, выбранная до открытия (клик по звезде в «Ждут отзыва»).
  initialRating?: number;
  // Загрузка фото/видео: без пропа кнопка «Загрузить» ведёт себя как раньше.
  onUploadMedia?: (file: File) => Promise<ReviewMediaItem>;
}) {
  const {
    t
  } = useTranslation();
  const [rating, setRating] = useState(initialRating ?? 0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [tag, setTag] = useState<string | null>(null);
  const [anonymous, setAnonymous] = useState(false);
  const [media, setMedia] = useState<ReviewMediaItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const mediaCount = media.length;
  const [busy, setBusy] = useState(false);
  const upload = async (files: FileList | null) => {
    if (!files?.length || !onUploadMedia) return;
    setUploading(true);
    try {
      for (const file of Array.from(files).slice(0, MAX_MEDIA - media.length)) {
        const item = await onUploadMedia(file);
        setMedia(prev => [...prev, item]);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.error", {
        defaultValue: "Ошибка"
      }));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const tags = [t("shop.leaveReview.tagGood", {
    defaultValue: "Отлично"
  }), t("shop.leaveReview.tagOk", {
    defaultValue: "Хорошо"
  }), t("shop.leaveReview.tagBad", {
    defaultValue: "Плохо"
  })];
  const submit = async () => {
    if (rating === 0) {
      toast.error(t("shop.leaveReview.noRating", {
        defaultValue: "Поставьте оценку"
      }));
      return;
    }
    setBusy(true);
    try {
      if (onSubmit) {
        await onSubmit({
          rating,
          text: text.trim(),
          anonymous,
          media
        });
      } else {
        // Ручки создания отзыва вне контекста заказа нет — сообщаем честно.
        toast.info(t("shop.leaveReview.unavailable", {
          defaultValue: "Отзыв можно оставить из заказа после доставки"
        }));
      }
      onOpenChange(false);
    } catch (e_0) {
      toast.error(e_0 instanceof Error ? e_0.message : t("common.error", {
        defaultValue: "Ошибка"
      }));
    } finally {
      setBusy(false);
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
			{/* Кадр 3870:20529: окно 440, r20, белое; шапка 20/23 w600 #161616 с
       крестиком 24 (поля 10/16); блоки с полями 10/16. Позиция окна —
       глобальное правило DS (A-21), своё не задаём. */}
			<DialogContent showCloseButton={false} overlayClassName="bg-black/20 backdrop-blur-[3px]" className="max-h-[92dvh] w-[calc(100vw-32px)] gap-0 overflow-y-auto rounded-[20px] border-0 bg-white p-0 shadow-none sm:max-w-[440px]">
				<div className="flex items-start gap-2.5 px-4 py-2.5">
					<DialogTitle className="min-w-0 flex-1 text-[20px] font-semibold leading-[23px] tracking-[-0.31px] text-ds-ink">
						{t("shop.leaveReview.title", {
            defaultValue: "Оставить отзыв"
          })}
					</DialogTitle>
					<DialogClose aria-label={t("common.close", {
          defaultValue: "Закрыть"
        })} className="grid size-6 shrink-0 place-items-center rounded-[4px] text-ds-primary outline-none hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-ds-focus">
						<XIcon />
					</DialogClose>
				</div>
				<DialogDescription className="sr-only">{subject.title}</DialogDescription>

				{/* «Товар в отзывах»: 408×149 r20 #F8F8F8, поля 8, зазор 12; фото
        100×133 r16 белое; название 16/20 w600, бренд 14/18, вариант и
        размер 12/16 #2E2E2E; звёзды 32 через 2. */}
				<div className="px-4 py-2.5">
					<div className="flex gap-3 rounded-[20px] bg-ds-quaternary p-2">
						<div className="h-[133px] w-[100px] shrink-0 overflow-hidden rounded-[16px] bg-white">
							{subject.photo && <img src={subject.photo} alt={subject.title} className="size-full object-contain" />}
						</div>
						<div className="flex min-w-0 flex-1 flex-col justify-between gap-2">
							<div className="flex flex-col gap-1">
								<span className="line-clamp-2 text-[16px] font-semibold leading-5 tracking-[-0.31px] text-ds-primary">
									{subject.title}
								</span>
								{subject.subtitle && <span className="text-[14px] leading-[18px] text-ds-primary">{subject.subtitle}</span>}
								{(subject.variant || subject.size) && <span className="flex flex-col gap-0.5 text-[12px] leading-4 text-ds-ink-2">
										{subject.variant && <span>{subject.variant}</span>}
										{subject.size && <span>{subject.size}</span>}
									</span>}
							</div>
							<div className="flex gap-0.5" onMouseLeave={() => setHover(0)}>
								{[1, 2, 3, 4, 5].map(n => <button key={n} type="button" onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} aria-label={t("shop.leaveReview.stars", {
                defaultValue: "Оценка {{n}} из 5",
                n
              })} aria-pressed={n === rating} className="rounded-[6px] outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">
										<Star32Icon size={32} className={cn("transition-colors", n <= (hover || rating) ? "text-osmix-yellow" : "text-ds-tertiary")} />
									</button>)}
							</div>
						</div>
					</div>
				</div>

				{/* Отзыв: подпись 14/18 w500 #171A24 64 %, поле r20 #1F1F1F 3 % (DS
        Textarea), чипы «Отлично / Хорошо / Плохо» (DS Chip). */}
				<div className="flex flex-col gap-4 px-4 py-2.5">
					<label className="flex flex-col gap-2">
						<span className="text-[14px] font-medium leading-[18px] text-ds-label">
							{t("shop.leaveReview.reviewLabel", {
              defaultValue: "Отзыв"
            })}
						</span>
						<Textarea value={text} onChange={e_1 => setText(e_1.target.value)} rows={2} maxLength={2000} placeholder={t("shop.leaveReview.placeholder", {
            defaultValue: "Ваша честная оценка и впечатления помогут нам и другим покупателям"
          })} className="min-h-[66px] resize-none" />
					</label>
					<div className="flex flex-wrap gap-2.5">
						{tags.map(label => <Chip key={label} selected={tag === label} onClick={() => setTag(tag === label ? null : label)}>
								{label}
							</Chip>)}
					</div>
				</div>

				{/* Фото или видео (N/10) + серая «Загрузить» M с download 16. */}
				<div className="flex flex-col gap-2.5 px-4 py-2.5">
					<span className="text-[14px] font-medium leading-[18px] text-ds-label">
						{t("shop.leaveReview.media", {
            defaultValue: "Фото или видео ({{count}}/{{max}})",
            count: mediaCount,
            max: MAX_MEDIA
          })}
					</span>
					{/* Превью 80×106 r4 */}
					{media.length > 0 && <div className="flex flex-wrap gap-2">
							{media.map((m, i) => <span key={`${m.url}-${i}`} className="relative h-[106px] w-20 overflow-hidden rounded-[4px] bg-[#1F1F1F]/[0.06]">
									{m.kind === "video" ? <video src={m.url} muted playsInline preload="metadata" className="size-full object-cover" /> : <img src={m.url} alt="" className="size-full object-cover" />}
									<button type="button" aria-label={t("shop.leaveReview.removeMedia", {
              defaultValue: "Убрать файл"
            })} onClick={() => setMedia(prev_0 => prev_0.filter((_, j) => j !== i))} className="absolute top-1 right-1 grid size-5 place-items-center rounded-full bg-black/50 text-white">
										<XSmall16Icon size={12} />
									</button>
								</span>)}
						</div>}
					{onUploadMedia && <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={e_2 => void upload(e_2.target.files)} />}
					<Button variant="gray" size="md" className="w-fit" disabled={uploading || mediaCount >= MAX_MEDIA} onClick={() => onUploadMedia ? fileRef.current?.click() : toast.info(t("shop.leaveReview.uploadSoon", {
          defaultValue: "Загрузка медиа появится позже"
        }))}>
						<Download16Icon />
						{t("shop.leaveReview.upload", {
            defaultValue: "Загрузить"
          })}
					</Button>
				</div>

				{/* bottomBar: чекбокс DS 20 + 16/21 w500, «Отправить» 48 на всю ширину. */}
				<div className="flex flex-col gap-5 px-4 pt-2.5 pb-4">
					<label className="flex cursor-pointer items-center gap-2.5">
						<Checkbox checked={anonymous} onCheckedChange={v => setAnonymous(v === true)} />
						<span className="text-[16px] font-medium leading-[21px] text-ds-primary">
							{t("shop.leaveReview.anonymous", {
              defaultValue: "Оставить отзыв анонимно"
            })}
						</span>
					</label>
					<Button block onClick={() => void submit()} disabled={busy}>
						{t("shop.leaveReview.submit", {
            defaultValue: "Отправить"
          })}
					</Button>
				</div>
			</DialogContent>
		</Dialog>;
}